"use client";

import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Image from "next/image";

type AuthBrandMarkProps = {
  className?: string;
  /** Accessible label for the logo image. Defaults to "MembEntry". */
  alt?: string;
  /** Show the MembEntry wordmark under the mark. Default true. */
  showWordmark?: boolean;
  /** Visual scale of the mark. Default "lg" for auth screens. */
  size?: "md" | "lg";
};

/**
 * Optical sizes tuned for auth hierarchy:
 * - Mark plate is large enough to own the form column without competing with the card.
 * - Inner padding (~14%) keeps the square SVG from kissing the plate edge.
 * - Wordmark sits one step below the mark, tagline one step below that.
 */
const sizeMap = {
  md: {
    box: "size-[4.5rem]",
    pad: "p-2.5",
    image: 52,
    title: "text-base",
    tag: "text-[0.6875rem]",
  },
  lg: {
    // 5.5rem mobile / 6rem desktop — authoritative without dominating a max-w-md form
    box: "size-[5.5rem] sm:size-24",
    pad: "p-3 sm:p-3.5",
    image: 72,
    title: "text-xl sm:text-[1.375rem]",
    tag: "text-[0.6875rem] sm:text-xs",
  },
} as const;

export function AuthBrandMark({
  className,
  alt = "MembEntry",
  showWordmark = true,
  size = "lg",
}: AuthBrandMarkProps) {
  const t = useTranslations();
  const dims = sizeMap[size];

  return (
    <div
      className={cn(
        "flex flex-col items-center",
        showWordmark ? "gap-3.5 sm:gap-4" : "gap-0",
        className,
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-center overflow-hidden",
          "rounded-[1.65rem] sm:rounded-[1.85rem]",
          "border border-white/70 bg-background/95",
          // Primary-tinted elevation (not the legacy red glow)
          "shadow-[0_14px_44px_-12px_color-mix(in_oklab,var(--primary)_28%,transparent),0_4px_14px_-4px_rgba(15,15,20,0.12)]",
          "ring-1 ring-primary/12",
          dims.box,
          dims.pad,
        )}
      >
        <Image
          src="/logo.svg"
          alt={alt}
          width={dims.image}
          height={dims.image}
          priority
          className="h-full w-full object-contain"
        />
      </div>
      {showWordmark ? (
        <div className="space-y-1 text-center">
          <p
            className={cn(
              "font-semibold tracking-[-0.02em] text-foreground",
              dims.title,
            )}
          >
            MembEntry
          </p>
          <p
            className={cn(
              "font-medium uppercase tracking-[0.2em] text-muted-foreground/90",
              dims.tag,
            )}
          >
            {t("auth.brandTagline")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
